// MppMarker — maximum-power-point dot for JV charts.
//
// Self-contained helper (2026-07 live-user feedback batch): draws a small
// filled dot at (Vmp, Jmp) in the SAME color as the curve it belongs to.
// Kept out of JVChartPanel/ComparisonModal so the marker logic doesn't
// collide with concurrent axis/tooltip work in those files — each chart
// adds a single `{mppDot({ ... })}` line inside its <LineChart>.
//
// IMPORTANT: mppDot is a plain function that RETURNS a recharts
// <ReferenceDot/> element. Call it inline inside the chart
// ({mppDot({...})}) rather than wrapping it in a component, so the chart
// receives a genuine ReferenceDot child.

import React from 'react';
import { ReferenceDot } from 'recharts';

// Resolve the MPP coordinate in the chart's sign convention.
// metrics.Jmp may be reported as a positive magnitude while the plotted
// light curve is negative in the power quadrant (backend Q4 convention),
// so the sign is taken from the JV point nearest Vmp when available.
export function mppPoint(metrics, jvCurve) {
  const vmp = Number(metrics?.Vmp);
  const jmpRaw = Number(metrics?.Jmp);
  if (!Number.isFinite(vmp) || !Number.isFinite(jmpRaw)) return null;
  let jmp = jmpRaw;
  if (Array.isArray(jvCurve) && jvCurve.length > 0) {
    let nearest = null;
    for (const pt of jvCurve) {
      if (!Number.isFinite(pt?.V) || !Number.isFinite(pt?.J)) continue;
      if (nearest === null || Math.abs(pt.V - vmp) < Math.abs(nearest.V - vmp)) {
        nearest = pt;
      }
    }
    if (nearest && nearest.J !== 0) {
      jmp = Math.sign(nearest.J) * Math.abs(jmpRaw);
    }
  }
  return { vmp, jmp };
}

// Returns a <ReferenceDot/> at the MPP, or null when Vmp/Jmp are absent.
//   metrics  — object carrying Vmp / Jmp
//   jvCurve  — [{V, J}] of the SAME curve (used only for the J sign)
//   color    — stroke color of that curve
//   yDivisor — optional: divide J by this (normalized comparison mode)
//   key      — react key when emitted in a list
export function mppDot({ metrics, jvCurve, color, yDivisor = null, key = 'mpp-dot' }) {
  const p = mppPoint(metrics, jvCurve);
  if (!p) return null;
  const y = (Number.isFinite(yDivisor) && yDivisor !== 0) ? p.jmp / yDivisor : p.jmp;
  return (
    <ReferenceDot
      key={key}
      x={p.vmp}
      y={y}
      r={4}
      fill={color}
      stroke={color}
      strokeWidth={1}
      isFront
    />
  );
}
